import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  House, Users, UserPlus, Buildings, Briefcase, CheckSquare,
  Medal, CalendarBlank, FileText, Envelope, Scales, CurrencyDollar,
  ChartBar, ClockCounterClockwise, ShieldCheck, MagnifyingGlass,
  SignOut, CaretLeft, CaretRight, Gear, List, Images, Wallet, ChatCircleDots
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9d663873-ece1-460a-abc5-f98dcd211b2a/artifacts/qilm7q3e_A087EB80-08A6-4F5B-B90E-AA189E10D04B.png";

const navSections = [
  {
    title: 'Overview',
    items: [
      { label: 'Dashboard', path: '/admin', icon: House },
    ]
  },
  {
    title: 'People',
    items: [
      { label: 'Members', path: '/admin/members', icon: Users },
      { label: 'Applicants', path: '/admin/applicants', icon: UserPlus },
    ]
  },
  {
    title: 'Organization',
    items: [
      { label: 'Units', path: '/admin/units', icon: Buildings },
      { label: 'Projects', path: '/admin/projects', icon: Briefcase },
      { label: 'Tasks', path: '/admin/tasks', icon: CheckSquare },
      { label: 'Appointments', path: '/admin/appointments', icon: Medal },
    ]
  },
  {
    title: 'Activities',
    items: [
      { label: 'Events', path: '/admin/events', icon: CalendarBlank },
      { label: 'Documents', path: '/admin/documents', icon: FileText },
      { label: 'Gallery', path: '/admin/gallery', icon: Images },
      { label: 'Communications', path: '/admin/communications', icon: Envelope },
      { label: 'Inquiries', path: '/admin/inquiries', icon: ChatCircleDots },
    ]
  },
  {
    title: 'Governance',
    items: [
      { label: 'Governance', path: '/admin/governance', icon: Scales },
      { label: 'Finance Ledger', path: '/admin/finance', icon: Wallet },
      { label: 'Contributions', path: '/admin/contributions', icon: CurrencyDollar },
    ]
  },
  {
    title: 'Administration',
    items: [
      { label: 'Reports', path: '/admin/reports', icon: ChartBar },
      { label: 'Audit Logs', path: '/admin/audit-logs', icon: ClockCounterClockwise },
      { label: 'Users & Roles', path: '/admin/users', icon: ShieldCheck },
    ]
  }
];

export const DashboardSidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside 
      className={`fixed left-0 top-0 h-screen bg-[#1A1614] border-r border-[#362E29] flex flex-col transition-all duration-300 z-40 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
      data-testid="dashboard-sidebar"
    >
      {/* Header */}
      <div className="p-4 border-b border-[#362E29]">
        <Link to="/admin" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Crest" className="h-10 w-10 object-contain" />
          {!collapsed && (
            <div>
              <p className="font-heading text-sm font-semibold text-[#F5F2EB]">Grand Priory</p>
              <p className="text-xs text-[#8A847A]">Back Office</p>
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {navSections.map((section) => (
          <div key={section.title} className="mb-4">
            {!collapsed && (
              <p className="px-4 mb-2 text-xs uppercase tracking-wider text-[#8A847A]">
                {section.title}
              </p>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-sm transition-colors ${
                    isActive 
                      ? 'bg-[#C5A059]/10 text-[#C5A059]' 
                      : 'text-[#8A847A] hover:text-[#F5F2EB] hover:bg-[#362E29]/50'
                  }`}
                  data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                  {!collapsed && <span className="text-sm">{item.label}</span>}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User & Collapse */}
      <div className="border-t border-[#362E29] p-4">
        {!collapsed && user && (
          <div className="mb-3">
            <p className="text-sm font-medium text-[#F5F2EB] truncate">{user.name}</p>
            <p className="text-xs text-[#8A847A] capitalize">{user.role?.replace('_', ' ')}</p>
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={handleLogout}
            className="flex-1 flex items-center justify-center gap-2 py-2 text-sm text-[#8A847A] hover:text-[#F5F2EB] transition-colors"
            data-testid="logout-button"
            title="Logout"
          >
            <SignOut size={18} />
            {!collapsed && <span>Logout</span>}
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 text-[#8A847A] hover:text-[#F5F2EB] transition-colors"
            data-testid="collapse-sidebar-button"
          >
            {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
          </button>
        </div>
      </div>
    </aside>
  );
};

export const DashboardHeader = ({ collapsed, setMobileMenuOpen }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/admin/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header 
      className={`fixed top-0 right-0 h-16 bg-white border-b border-[#D8D2C4] flex items-center justify-between px-6 z-30 transition-all duration-300 ${
        collapsed ? 'left-16' : 'left-64'
      }`}
      data-testid="dashboard-header"
    >
      <div className="flex items-center gap-4">
        <button
          className="lg:hidden p-2 text-[#4A453E]"
          onClick={() => setMobileMenuOpen(true)}
          data-testid="mobile-menu-toggle"
        >
          <List size={24} />
        </button>
        <form onSubmit={handleSearch} className="relative hidden sm:block">
          <MagnifyingGlass 
            size={18} 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A847A]" 
          />
          <input
            type="text"
            placeholder="Search members, projects, documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-80 bg-[#FAF9F6] border border-[#D8D2C4] text-sm focus:outline-none focus:border-[#C5A059]"
            data-testid="global-search-input"
          />
        </form>
      </div>
      <div className="flex items-center gap-4">
        <Link
          to="/"
          className="text-sm text-[#4A453E] hover:text-[#7B1818] transition-colors"
          data-testid="view-public-site-link"
        >
          View Public Site
        </Link>
        <Link
          to="/admin/settings"
          className="p-2 text-[#4A453E] hover:text-[#7B1818] transition-colors"
          data-testid="settings-link"
        >
          <Gear size={20} />
        </Link>
      </div>
    </header>
  );
};

export const DashboardLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full">
            <DashboardSidebar collapsed={false} setCollapsed={() => {}} />
          </div>
        </div>
      )}

      {/* Header */}
      <DashboardHeader collapsed={collapsed} setMobileMenuOpen={setMobileMenuOpen} />

      {/* Main Content */}
      <main 
        className={`pt-16 min-h-screen transition-all duration-300 ${
          collapsed ? 'lg:pl-16' : 'lg:pl-64'
        }`}
      >
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
